/**
 * Env-var parsing for the staff support console, trimmed to the two
 * vars the console actually reads — there is no service worker and no
 * crash reporter on this internal surface.
 *
 * - `VITE_CLERK_PUBLISHABLE_KEY` — Clerk publishable key for the client
 *   SDK. Required at deploy time; in dev the `developmentPlaceholder`
 *   fallback keeps `pnpm dev` running with a clearly-named stub so the
 *   sign-in flow renders rather than crashing on missing config.
 * - `VITE_API_BASE_URL` — the Hono+tRPC API origin. Defaults to
 *   `http://localhost:8080` for local dev; deploys set this to the
 *   public API URL.
 * - `VITE_CLERK_DASHBOARD_USER_URL` — optional Clerk dashboard
 *   deep-link template for the invocation list's User column. Must
 *   contain the literal `{userId}` placeholder, e.g.
 *   `https://dashboard.clerk.com/apps/<appId>/instances/<insId>/users/{userId}`.
 *   The app/instance ids are environment-specific (iTest vs prod) and
 *   not derivable from the publishable key, so they're supplied here.
 *   Absent (or missing the placeholder) → the column renders plain text.
 *
 * The parser is a pure function over a `Record<string, string |
 * undefined>` shape so unit tests don't need a Vite runtime. The
 * runtime call site is `parseEnv(import.meta.env)`.
 */

export type SupportEnv = {
  readonly clerkPublishableKey: string;
  readonly clerkPublishableKeySource: "configured" | "development-placeholder";
  readonly apiBaseUrl: string;
  /**
   * Clerk dashboard deep-link template (contains `{userId}`), or
   * `undefined` when unconfigured / malformed.
   */
  readonly clerkDashboardUserUrlTemplate: string | undefined;
  readonly mode: "development" | "production" | "test";
};

export type RawEnv = {
  readonly VITE_CLERK_PUBLISHABLE_KEY?: string | undefined;
  readonly VITE_API_BASE_URL?: string | undefined;
  readonly VITE_CLERK_DASHBOARD_USER_URL?: string | undefined;
  readonly MODE?: string | undefined;
  readonly PROD?: boolean | undefined;
  readonly DEV?: boolean | undefined;
};

/** The placeholder a dashboard-URL template must contain to be usable. */
const USER_ID_PLACEHOLDER = "{userId}";

/**
 * Clearly-named dev placeholder. Real Clerk publishable keys are
 * prefixed `pk_test_` / `pk_live_` and the suffix is a base64-encoded
 * frontend-API host terminated by `$` — the Clerk SDK *validates* the
 * shape at `<ClerkProvider>` mount and throws synchronously on a
 * malformed key, which would unmount the React tree.
 *
 * The suffix below decodes to `placeholder.clerk.invalid$`. The
 * `.invalid` TLD (RFC 2606) never resolves, so Clerk's async script
 * load fails fast (auth doesn't actually work in placeholder mode) but
 * the SDK passes its boot-time validation and the page renders.
 * `clerkPublishableKeySource` flags this mode so deploy scripts can
 * still refuse to ship the placeholder.
 */
const DEV_CLERK_PLACEHOLDER = "pk_test_cGxhY2Vob2xkZXIuY2xlcmsuaW52YWxpZCQ";

const DEFAULT_API_BASE_URL = "http://localhost:8080";

export const parseEnv = (raw: RawEnv): SupportEnv => {
  const mode = parseMode(raw);

  const configuredKey = nonEmpty(raw.VITE_CLERK_PUBLISHABLE_KEY);
  const clerkPublishableKey = configuredKey ?? DEV_CLERK_PLACEHOLDER;
  const clerkPublishableKeySource: SupportEnv["clerkPublishableKeySource"] =
    configuredKey !== undefined ? "configured" : "development-placeholder";

  const apiBaseUrl = nonEmpty(raw.VITE_API_BASE_URL) ?? DEFAULT_API_BASE_URL;

  // Only accept a template that actually carries the `{userId}` slot —
  // a misconfigured value (e.g. the bare dashboard root) would link
  // every row to the same place, so we drop it to plain text instead.
  const rawTemplate = nonEmpty(raw.VITE_CLERK_DASHBOARD_USER_URL);
  const clerkDashboardUserUrlTemplate =
    rawTemplate !== undefined && rawTemplate.includes(USER_ID_PLACEHOLDER)
      ? rawTemplate
      : undefined;

  return {
    clerkPublishableKey,
    clerkPublishableKeySource,
    apiBaseUrl,
    clerkDashboardUserUrlTemplate,
    mode,
  };
};

const parseMode = (raw: RawEnv): SupportEnv["mode"] => {
  const explicit = nonEmpty(raw.MODE);
  if (explicit === "production" || explicit === "test") {
    return explicit;
  }
  if (raw.PROD === true) return "production";
  return "development";
};

const nonEmpty = (raw: string | undefined): string | undefined => {
  if (raw === undefined) return undefined;
  const trimmed = raw.trim();
  return trimmed.length === 0 ? undefined : trimmed;
};
